import type { AnalyzeDocumentResponse } from '../common/service/document.service';
import type { DocumentType } from '../model';
import type { SuggestedDocument, SuggestedDocumentDraft } from './document-workbench.model';

export interface SuggestedDocumentType {
  documentTypeId: string;
  documentTypeName?: string | null;
  documentTypeConfidence?: number | null;
}

/** A single type is valid only when suggestions cover all requested pages with the same ID. */
export function selectSuggestedDocumentType(
  analysis: AnalyzeDocumentResponse['analysis'] | undefined,
  requestedPages: readonly number[]
): SuggestedDocumentType | undefined {
  const suggestions = analysis?.suggestedDocuments;
  if (!Array.isArray(suggestions) || !suggestions.length || !requestedPages.length) return undefined;
  const requested = new Set(requestedPages);
  const covered = new Set<number>();
  let selected: SuggestedDocumentType | undefined;
  for (const suggestion of suggestions) {
    if (!suggestion?.documentTypeId || !Array.isArray(suggestion.pages) || !suggestion.pages.length) return undefined;
    if (selected && selected.documentTypeId !== suggestion.documentTypeId) return undefined;
    for (const page of suggestion.pages) {
      if (!Number.isInteger(page) || !requested.has(page) || covered.has(page)) return undefined;
      covered.add(page);
    }
    selected ??= {
      documentTypeId: suggestion.documentTypeId,
      documentTypeName: suggestion.documentType,
      documentTypeConfidence: suggestion.documentTypeConfidence,
    };
    if (suggestion.documentTypeConfidence != null) {
      selected.documentTypeConfidence = Math.min(
        selected.documentTypeConfidence ?? suggestion.documentTypeConfidence,
        suggestion.documentTypeConfidence
      );
    }
  }
  return covered.size === requested.size ? selected : undefined;
}

/** Resolve valid, disjoint suggestions against the types available in this section. */
export function buildSuggestedDocumentDrafts(
  suggestions: readonly SuggestedDocument[],
  availablePages: readonly number[],
  types: readonly DocumentType[]
): SuggestedDocumentDraft[] {
  const available = new Set(availablePages);
  const claimed = new Set<number>();
  const drafts: SuggestedDocumentDraft[] = [];
  for (const suggestion of suggestions) {
    if (!suggestion || !Array.isArray(suggestion.pages) || !suggestion.pages.length) continue;
    const pages = [...new Set(suggestion.pages)].sort((a, b) => a - b);
    if (pages.some((page) => !Number.isInteger(page) || !available.has(page) || claimed.has(page))) continue;
    const type = types.find((item) => item.id === suggestion.documentTypeId);
    if (!type) continue;
    pages.forEach((page) => claimed.add(page));
    drafts.push({ pages, documentTypeId: type.id, documentTypeName: type.name });
  }
  return drafts;
}
